import { emptyApi } from "./emptyApi";
import qs from "qs";

export type PerkTag = {
  bg_color_hex: string;
  color_hex: string;
  tag_category: number;
  title: string;
  uuid: string;
};

export type AdminPerk = {
  perk_id: string;
  name: string;
  website?: string;
  contact_email?: string;
  description?: string;
  heading?: string;
  offer_description?: string;
  linkedin_url?: string;
  perk_type: string;
  icon?: string;
  icon_bg_color?: string;
  state: number;
  slug: string;
  added_by?: string;
  created_at: string;
  instructions?: string;
  tags: PerkTag[];
  promo_code?: string;
  promo_code_redeem_url?: string;
  special_url?: string;
  intro_emails?: string[];
  generic_instruction?: string;
  special_email?: string;
  redeem_instruction?: string;
};

type AdminPerksResponse = {
  perks: AdminPerk[];
  current_page: number;
  total_count: number;
  total_pages: number;
};

type GetAdminPerksProps = {
  pageNumber?: number;
  state?: number | null;
  searchTerm?: string | null;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAdminPerks: build.query<AdminPerksResponse, GetAdminPerksProps>({
      query: ({ pageNumber, state, searchTerm }) => {
        const queryUrl = qs.stringify(
          {
            page_num: pageNumber,
            state: state,
            search_term: searchTerm,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return { url: `api/inkle/reference/admin/perks/${queryUrl}` };
      },
      providesTags: ["AdminPerks"],
    }),

    updatePerkState: build.mutation<
      AdminPerk,
      { perkId: string; state: number }
    >({
      query: ({ perkId, state }) => ({
        url: `api/inkle/reference/admin/perks/${perkId}/`,
        method: "PUT",
        body: { state },
      }),
      invalidatesTags: ["AdminPerks"],
    }),

    adminCreatePerk: build.mutation<AdminPerk, FormData>({
      query: (formData) => ({
        url: `api/inkle/reference/admin/perks/`,
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["AdminPerks"],
    }),

    adminUpdatePerk: build.mutation<
      AdminPerk,
      { perkId: string; formData: FormData }
    >({
      query: ({ perkId, formData }) => ({
        url: `api/inkle/reference/admin/perks/${perkId}/`,
        method: "PATCH",
        body: formData,
      }),
      invalidatesTags: ["AdminPerks"],
    }),

    addPerkTag: build.mutation<AdminPerk, { perkId: string; tagId: string }>({
      query: ({ perkId, tagId }) => ({
        url: `api/inkle/reference/admin/perks/${perkId}/tags/${tagId}/`,
        method: "POST",
      }),
      invalidatesTags: ["AdminPerks"],
    }),

    removePerkTag: build.mutation<AdminPerk, { perkId: string; tagId: string }>(
      {
        query: ({ perkId, tagId }) => ({
          url: `api/inkle/reference/admin/perks/${perkId}/tags/${tagId}/`,
          method: "DELETE",
        }),
        invalidatesTags: ["AdminPerks"],
      }
    ),

    getAllPerkTags: build.query<PerkTag[], void>({
      query: () => "/api/inkle/tags/PERK_TAG/",
      providesTags: ["PerkTags"],
    }),

    createPerkTag: build.mutation<
      PerkTag,
      { title: string; color_hex?: string; bg_color_hex?: string }
    >({
      query: (body) => ({
        url: `api/inkle/tags/create/PERK_TAG/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["PerkTags"],
    }),

    suggestPerk: build.mutation<
      void,
      { groupId: string; name: string; website: string; notes: string }
    >({
      query: ({ groupId, name, website, notes }) => ({
        url: `api/inkle/reference/group/${groupId}/suggest-perk/`,
        method: "POST",
        body: { name, website, notes },
      }),
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetAdminPerksQuery,
  useUpdatePerkStateMutation,
  useAdminCreatePerkMutation,
  useAdminUpdatePerkMutation,
  useAddPerkTagMutation,
  useRemovePerkTagMutation,
  useGetAllPerkTagsQuery,
  useCreatePerkTagMutation,
  useSuggestPerkMutation,
} = extendedApi;
